import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, TrendingUp, Trash2 } from 'lucide-react';
import { Holding, updateHolding, computeTotals } from '../../services/holdingsService';
import { runKeeperFlow, KeeperAnalysisMode } from '../../services/keeperService';
import { KeeperAssessment } from '../../types/geminiTypes';
import { KeeperCardMobile } from './KeeperCardMobile';
import { StalenessPrompt } from './StalenessPrompt';
import { KeeperErrorBand, KeeperError, describeKeeperError } from './KeeperErrorBand';
import { LotManagerModal } from './LotManagerModal';
import { cn } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';

interface HoldingCardMobileProps {
  holding: Holding;
  uid: string;
  onRemove: (ticker: string, docId?: string) => void;
  onAnalyze: (ticker: string) => void;
  onKeeperComplete: (ticker: string, verdict: 'KEEP' | 'WATCH' | 'EXIT') => void;
  currentPrice?: number | null;
}

const verdictStyles = {
  KEEP: 'bg-success-50 text-success-700 border-success-100',
  WATCH: 'bg-warning-50 text-warning-700 border-warning-100',
  EXIT: 'bg-danger-50 text-danger-700 border-danger-100',
};

export function HoldingCardMobile(props: HoldingCardMobileProps) {
  const { holding, uid, onRemove, onAnalyze, onKeeperComplete } = props;
  const [currentPrice, setCurrentPrice] = useState<number | null>(props.currentPrice ?? null);
  const [priceLoading, setPriceLoading] = useState<boolean>(props.currentPrice == null);
  const [priceError, setPriceError] = useState(false);
  const [keeperLoading, setKeeperLoading] = useState(false);
  const [material8Ks, setMaterial8Ks] = useState<Array<{ filingDate: string; descriptions: string[] }>>([]);
  const [keeperProgress, setKeeperProgress] = useState('');
  const [keeperAssessment, setKeeperAssessment] = useState<KeeperAssessment | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showStalenessPrompt, setShowStalenessPrompt] = useState(false);
  const [stalenessAge, setStalenessAge] = useState(0);
  const [keeperError, setKeeperError] = useState<KeeperError | null>(null);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { totalShares, avgCostPerShare } = holding.lots ? computeTotals(holding.lots) : { totalShares: 0, avgCostPerShare: 0 };

  const fetchPrice = useCallback(async () => {
    setPriceLoading(true);
    setPriceError(false);
    try {
      const res = await fetch(`/api/fmp/stable/quote?symbol=${holding.ticker}`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0 && data[0].price) {
          setCurrentPrice(data[0].price);
        }
      }
    } catch {
      console.error("Failed to fetch price for", holding.ticker);
      setPriceError(true);
    } finally {
      setPriceLoading(false);
    }
  }, [holding.ticker]);

  useEffect(() => {
    if (props.currentPrice === undefined) {
      setPriceLoading(true);
      setPriceError(false);
    } else if (props.currentPrice === null) {
      setPriceLoading(false);
      setPriceError(true);
      setCurrentPrice(null);
    } else {
      setCurrentPrice(props.currentPrice);
      setPriceLoading(false);
      setPriceError(false);
    }
  }, [props.currentPrice]);

  const handleAssessPosition = async (mode: KeeperAnalysisMode = 'auto') => {
    if (currentPrice === null) return;
    setShowStalenessPrompt(false);
    setKeeperError(null);
    setKeeperLoading(true);
    setKeeperProgress('Checking for existing analysis...');
    try {
      const result = await runKeeperFlow(
        { ticker: holding.ticker, currentPrice },
        uid,
        (msg) => setKeeperProgress(msg),
        mode
      );
      // 'auto' with a stale analysis returns no assessment — prompt the user.
      if (result.stalenessWarning && result.keeperAssessment === null) {
        setStalenessAge(result.analysisAgeDays ?? 0);
        setShowStalenessPrompt(true);
        setKeeperLoading(false);
        return;
      }
      setKeeperAssessment(result.keeperAssessment);
      if (result.keeperAssessment) {
        setIsExpanded(true);
        await updateHolding(uid, holding.ticker, {
          lastKeeperVerdict: result.keeperAssessment.verdict,
          lastKeeperDate: new Date().toISOString(),
          lastAssessment: result.keeperAssessment
        });
        onKeeperComplete(holding.ticker, result.keeperAssessment.verdict);
      }
    } catch (err) {
      console.error('[KEEPER] Assessment failed:', err);
      setKeeperError(describeKeeperError(err));
    } finally {
      setKeeperLoading(false);
    }
  };

  const gainLoss = currentPrice !== null && avgCostPerShare > 0
    ? ((currentPrice - avgCostPerShare) / avgCostPerShare) * 100
    : null;

  useEffect(() => {
    if (!holding.lastOracleAnalysisDate) return;
    let cancelled = false;
    fetch(
      `/api/edgar/8k-check?ticker=${encodeURIComponent(holding.ticker)}&since=${encodeURIComponent(holding.lastOracleAnalysisDate)}`
    )
      .then(r => r.json())
      .then(data => {
        if (!cancelled && Array.isArray(data.filings)) {
          setMaterial8Ks(data.filings);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [holding.ticker, holding.lastOracleAnalysisDate]);

  const activeAssessment = keeperAssessment || holding.lastAssessment;

  return (
    <>
      <div className="oi-card p-4 space-y-3">

        {/* Header row */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-medium text-base">{holding.ticker}</span>
              {holding.lastKeeperVerdict && (
                <span className={cn(
                  "text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-md border",
                  verdictStyles[holding.lastKeeperVerdict]
                )}>
                  {holding.lastKeeperVerdict}
                </span>
              )}
            </div>
            <p className="text-xs text-ink-500 mt-0.5 leading-tight">{holding.companyName}</p>
            {material8Ks.length > 0 && (
              <p
                className="text-[9px] font-medium text-warning-700 uppercase tracking-wider mt-0.5 cursor-default"
                title={`Material SEC filing since last analysis: ${material8Ks.map(f => `${f.filingDate} — ${f.descriptions.join(', ')}`).join(' | ')}`}
              >
                ⚠ Material Filing
              </p>
            )}
          </div>

          {/* Current price */}
          <div className="text-right">
            {priceLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-ink-300" />
            ) : priceError ? (
              <button onClick={fetchPrice} className="text-xs text-danger-600 underline">Retry</button>
            ) : currentPrice !== null ? (
              <div>
                <p className="font-medium text-sm">${safeToFixed(currentPrice, 2)}</p>
                {gainLoss !== null && (
                  <p className={cn(
                    "text-[10px] font-medium",
                    gainLoss >= 0 ? "text-success-600" : "text-danger-600"
                  )}>
                    {gainLoss >= 0 ? '+' : ''}{safeToFixed(gainLoss, 1)}%
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Position details */}
        {(totalShares > 0 || avgCostPerShare > 0) && (
          <div className="flex gap-4 text-xs text-ink-500">
            {totalShares > 0 && (
              <span>{totalShares.toLocaleString()} shares</span>
            )}
            {avgCostPerShare > 0 && (
              <span>Basis: ${safeToFixed(avgCostPerShare, 2)}</span>
            )}
          </div>
        )}

        {/* Staleness prompt */}
        {showStalenessPrompt && (
          <StalenessPrompt
            ageDays={stalenessAge}
            onRunFresh={() => { setShowStalenessPrompt(false); handleAssessPosition('fresh'); }}
            onUseOld={() => { setShowStalenessPrompt(false); handleAssessPosition('stale'); }}
          />
        )}

        {/* Fresh-analysis failure band */}
        {keeperError && !keeperLoading && (
          <KeeperErrorBand
            error={keeperError}
            lastKeeperDate={holding.lastKeeperDate}
            hasPriorAssessment={!!activeAssessment}
          />
        )}

        {/* Keeper loading progress */}
        {keeperLoading && (
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>{keeperProgress}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => handleAssessPosition()}
            disabled={keeperLoading || priceLoading || currentPrice === null || priceError}
            className={cn(
              "flex-1 text-xs font-medium uppercase tracking-wider py-2 rounded-md transition-colors",
              keeperLoading || priceLoading || currentPrice === null || priceError
                ? "bg-surface-2 text-ink-500 cursor-not-allowed"
                : "bg-oxblood text-paper hover:bg-oxblood-800"
            )}
          >
            {keeperLoading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Assess"}
          </button>

          <button
            onClick={() => setShowEditModal(true)}
            className="flex-1 text-xs font-medium uppercase tracking-wider py-2 rounded-md bg-surface-2 text-ink-600 hover:bg-surface transition-colors"
          >
            Lots
          </button>

          <button
            onClick={() => onAnalyze(holding.ticker)}
            className="flex-1 text-xs font-medium uppercase tracking-wider py-2 rounded-md bg-surface-2 text-ink-600 hover:bg-surface transition-colors flex items-center justify-center gap-1"
          >
            <TrendingUp className="w-3 h-3" />
            Analyze
          </button>

          {showConfirmRemove ? (
            <>
              <button
                onClick={() => onRemove(holding.ticker, holding._docId)}
                className="text-xs font-medium uppercase tracking-wider px-3 py-2 rounded-md bg-danger-600 text-paper hover:bg-danger-700 transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setShowConfirmRemove(false)}
                className="text-xs font-medium uppercase tracking-wider px-3 py-2 rounded-md bg-surface-2 text-ink-600 hover:bg-surface transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowConfirmRemove(true)}
              className="p-2 rounded-md bg-surface-2 text-ink-500 hover:bg-danger-50 hover:text-danger-600 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Keeper assessment result */}
        {!keeperLoading && (
          <div className="pt-2">
            {activeAssessment ? (
              <>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="w-full text-xs text-ink-600 font-medium tracking-wide transition-colors text-center py-2.5 bg-surface-2 hover:bg-surface rounded-md"
                >
                  View Assessment Details
                </button>
                {holding.lastKeeperDate && (
                  <p className="w-full text-[10px] text-ink-500 text-center mt-1.5 uppercase tracking-wider">
                    Assessed: {new Date(holding.lastKeeperDate).toLocaleDateString()}
                  </p>
                )}
              </>
            ) : (
              holding.lastKeeperDate && (
                <p className="w-full text-[10px] text-ink-500 text-center mt-1 uppercase tracking-wider">
                  Last Assessed: {new Date(holding.lastKeeperDate).toLocaleDateString()}
                </p>
              )
            )}
          </div>
        )}

      </div>

      {/* Keeper bottom sheet */}
      {isExpanded && activeAssessment && (
        <KeeperCardMobile
          assessment={activeAssessment}
          onDismiss={() => setIsExpanded(false)}
        />
      )}

      {showEditModal && (
        <LotManagerModal
          uid={uid}
          holding={holding}
          onClose={() => setShowEditModal(false)}
          onSaved={() => setShowEditModal(false)}
        />
      )}
    </>
  );
}
